import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CalendarClock,
  ChevronDown,
  History,
  Minus,
  Plus,
  X,
} from "lucide-react";
import { useState, type FormEvent } from "react";
import { PageHeader } from "../components/PageHeader";
import { RetryButton, StatePanel } from "../components/StatePanel";
import { StatusChip } from "../components/StatusChip";
import { api, formatDate, mutationKey } from "../lib/api";
import type { AssignmentItem, ListResponse } from "../lib/types";

function formText(data: FormData, name: string): string {
  const value = data.get(name);
  return typeof value === "string" ? value : "";
}

export function AssignmentsPage() {
  const [state, setState] = useState("");
  const query = useQuery({
    queryKey: ["assignments", state],
    queryFn: () =>
      api<ListResponse<AssignmentItem>>(
        `/assignments${state === "" ? "" : `?state=${state}`}`,
      ),
  });
  return (
    <>
      <PageHeader
        eyebrow="Manager workspace"
        title="Assignments"
        description="Responsibility, plant quantity, and post allowance in one auditable record."
      />
      <div className="filter-bar">
        <label>
          Assignment state
          <select
            value={state}
            onChange={(event) => setState(event.target.value)}
          >
            <option value="">All states</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="completed">Completed</option>
            <option value="revoked">Revoked</option>
          </select>
        </label>
      </div>
      {query.isLoading ? (
        <StatePanel
          kind="loading"
          title="Loading assignments"
          detail="Reading responsibility and allowance balances."
        />
      ) : query.isError || query.data === undefined ? (
        <StatePanel
          kind="error"
          title="Assignments are unavailable"
          detail="Try loading the assignment register again."
          action={<RetryButton onClick={() => void query.refetch()} />}
        />
      ) : query.data.items.length === 0 ? (
        <StatePanel
          kind="empty"
          title="No matching assignments"
          detail="Change the filter or assign an approved request."
        />
      ) : (
        <div className="record-list">
          {query.data.items.map((assignment) => (
            <AssignmentRecord key={assignment.id} assignment={assignment} />
          ))}
        </div>
      )}
    </>
  );
}

function AssignmentRecord({
  assignment,
}: {
  readonly assignment: AssignmentItem;
}) {
  const queryClient = useQueryClient();
  const [editingWindow, setEditingWindow] = useState(false);
  const [pendingTransition, setPendingTransition] = useState<
    "pause" | "resume" | "transfer" | "complete" | "revoke" | null
  >(null);
  const adjust = useMutation({
    mutationFn: (quantity: number) =>
      api(`/assignments/${assignment.id}/allowance-adjustments`, {
        method: "POST",
        idempotencyKey: mutationKey("allowance"),
        body: JSON.stringify({
          quantity,
          reason:
            quantity > 0
              ? "Additional reporting window approved"
              : "Unused reporting slot revoked",
          expected_resource_version: assignment.current_version,
        }),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["assignments"] });
    },
  });
  const transition = useMutation({
    mutationFn: ({
      action,
      primaryPlanterId,
      startsAt,
      endsAt,
      suppliedReason,
    }: {
      readonly action:
        | "pause"
        | "resume"
        | "transfer"
        | "complete"
        | "revoke"
        | "update_window";
      readonly primaryPlanterId?: string;
      readonly startsAt?: string;
      readonly endsAt?: string | null;
      readonly suppliedReason?: string;
    }) => {
      const reason = suppliedReason?.trim();
      if (reason === undefined || reason.length < 3) {
        throw new Error("A reason of at least 3 characters is required.");
      }
      return api(`/assignments/${assignment.id}`, {
        method: "PATCH",
        idempotencyKey: mutationKey(`assignment-${action}`),
        body: JSON.stringify({
          action,
          reason,
          expected_resource_version: assignment.current_version,
          ...(primaryPlanterId === undefined
            ? {}
            : { primary_planter_id: primaryPlanterId }),
          ...(startsAt === undefined ? {} : { starts_at: startsAt }),
          ...(endsAt === undefined ? {} : { ends_at: endsAt }),
        }),
      });
    },
    onSuccess: async (_, variables) => {
      if (variables.action === "update_window") {
        setEditingWindow(false);
      } else {
        setPendingTransition(null);
      }
      await queryClient.invalidateQueries({ queryKey: ["assignments"] });
    },
  });
  const openTransition = (
    action: "pause" | "resume" | "transfer" | "complete" | "revoke",
  ) => {
    transition.reset();
    setPendingTransition(action);
  };
  return (
    <article className="record-row assignment-record">
      <div>
        <div className="record-title">
          <h2>{assignment.site_name}</h2>
          <StatusChip state={assignment.state} />
        </div>
        <p>
          {assignment.declared_plant_quantity.toLocaleString()} plants under
          care · Planter {assignment.primary_planter_id}
        </p>
        <small>
          Reporting window: {formatDate(assignment.starts_at)} –{" "}
          {assignment.ends_at === null
            ? "Open ended"
            : formatDate(assignment.ends_at)}
          <br />
          Next follow-up: {formatDate(assignment.next_follow_up_at)}
        </small>
      </div>
      <div className="allowance">
        <span>
          <strong>{assignment.remaining_slots}</strong> remaining
        </span>
        <small>
          {assignment.consumed_slots} used of {assignment.granted_slots}
        </small>
        <div className="stepper" aria-label="Adjust unused slots">
          <button
            type="button"
            aria-label="Remove one unused slot"
            disabled={adjust.isPending || assignment.remaining_slots === 0}
            onClick={() => adjust.mutate(-1)}
          >
            <Minus aria-hidden="true" />
          </button>
          <button
            type="button"
            aria-label="Add one slot"
            disabled={adjust.isPending}
            onClick={() => adjust.mutate(1)}
          >
            <Plus aria-hidden="true" />
          </button>
        </div>
        <div className="row-actions">
          {["active", "paused"].includes(assignment.state) ? (
            <button
              className="text-button"
              type="button"
              onClick={() => {
                transition.reset();
                setEditingWindow(true);
              }}
            >
              <CalendarClock aria-hidden="true" />
              Edit window
            </button>
          ) : null}
          {assignment.state === "active" ? (
            <button
              className="text-button"
              type="button"
              onClick={() => openTransition("pause")}
            >
              Pause
            </button>
          ) : null}
          {assignment.state === "paused" ? (
            <button
              className="text-button"
              type="button"
              onClick={() => openTransition("resume")}
            >
              Resume
            </button>
          ) : null}
          {["active", "paused"].includes(assignment.state) ? (
            <>
              <button
                className="text-button"
                type="button"
                onClick={() => openTransition("transfer")}
              >
                Transfer
              </button>
              <button
                className="text-button"
                type="button"
                onClick={() => openTransition("complete")}
              >
                Complete
              </button>
              <button
                className="text-button danger-text"
                type="button"
                onClick={() => openTransition("revoke")}
              >
                Revoke
              </button>
            </>
          ) : null}
        </div>
        {adjust.isError ||
        (transition.isError && !editingWindow && pendingTransition === null) ? (
          <small className="form-error" role="alert">
            {(adjust.error ?? transition.error)?.message}
          </small>
        ) : null}
      </div>
      <AllowanceHistory assignment={assignment} />
      {editingWindow ? (
        <AssignmentWindowDialog
          assignment={assignment}
          pending={transition.isPending}
          error={transition.isError ? transition.error.message : null}
          onClose={() => {
            transition.reset();
            setEditingWindow(false);
          }}
          onSave={({ startsAt, endsAt, reason }) =>
            transition.mutate({
              action: "update_window",
              startsAt,
              endsAt,
              suppliedReason: reason,
            })
          }
        />
      ) : null}
      {pendingTransition === null ? null : (
        <AssignmentTransitionDialog
          assignment={assignment}
          action={pendingTransition}
          pending={transition.isPending}
          error={transition.isError ? transition.error.message : null}
          onClose={() => {
            transition.reset();
            setPendingTransition(null);
          }}
          onSave={({ reason, primaryPlanterId }) =>
            transition.mutate({
              action: pendingTransition,
              suppliedReason: reason,
              ...(primaryPlanterId === undefined ? {} : { primaryPlanterId }),
            })
          }
        />
      )}
    </article>
  );
}

function AssignmentTransitionDialog({
  assignment,
  action,
  pending,
  error,
  onClose,
  onSave,
}: {
  readonly assignment: AssignmentItem;
  readonly action: "pause" | "resume" | "transfer" | "complete" | "revoke";
  readonly pending: boolean;
  readonly error: string | null;
  readonly onClose: () => void;
  readonly onSave: (value: {
    readonly reason: string;
    readonly primaryPlanterId?: string;
  }) => void;
}) {
  const candidates = useQuery({
    queryKey: ["assignment-candidates", assignment.site_id],
    queryFn: () =>
      api<{
        readonly planters: readonly {
          readonly id: string;
          readonly display_name: string;
        }[];
      }>(
        `/assignment-candidates?site_id=${encodeURIComponent(assignment.site_id)}`,
      ),
    enabled: action === "transfer",
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const reason = formText(data, "reason").trim();
    const primaryPlanterId = formText(data, "primary_planter_id");
    onSave({
      reason,
      ...(action === "transfer" ? { primaryPlanterId } : {}),
    });
  };
  const impacts = {
    pause: "Posting stops until a manager resumes this assignment.",
    resume: "Posting resumes inside the assignment reporting window.",
    transfer:
      "Future responsibility moves to the selected eligible planter; existing posts, points, and allowance history remain attached.",
    complete:
      "The assignment closes as completed and no further official posts can be added.",
    revoke:
      "The assignment closes as revoked and unused allowance is removed from future use.",
  } as const;
  return (
    <div className="dialog-layer" role="presentation">
      <form
        className="dialog assignment-transition-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`transition-title-${assignment.id}`}
        aria-describedby={`transition-impact-${assignment.id}`}
        onSubmit={submit}
        onKeyDown={(event) => {
          if (event.key === "Escape" && !pending) onClose();
        }}
      >
        <div className="dialog-title">
          <div>
            <p className="eyebrow">{assignment.site_name}</p>
            <h2 id={`transition-title-${assignment.id}`}>
              Confirm assignment {action}
            </h2>
          </div>
          <button
            className="icon-button"
            type="button"
            aria-label="Close assignment action"
            disabled={pending}
            onClick={onClose}
          >
            <X aria-hidden="true" />
          </button>
        </div>
        <p id={`transition-impact-${assignment.id}`}>{impacts[action]}</p>
        {action === "transfer" ? (
          candidates.isLoading ? (
            <StatePanel
              kind="loading"
              title="Loading eligible planters"
              detail="Checking active accounts in this assignment scope."
            />
          ) : candidates.isError || candidates.data === undefined ? (
            <StatePanel
              kind="error"
              title="Eligible planters unavailable"
              detail="No transfer was made. Close this dialog and try again."
            />
          ) : (
            <label>
              New primary planter
              <select name="primary_planter_id" required autoFocus>
                <option value="">Select an eligible planter</option>
                {candidates.data.planters
                  .filter(
                    (planter) => planter.id !== assignment.primary_planter_id,
                  )
                  .map((planter) => (
                    <option key={planter.id} value={planter.id}>
                      {planter.display_name}
                    </option>
                  ))}
              </select>
            </label>
          )
        ) : null}
        <label>
          Change reason
          <textarea
            name="reason"
            required
            minLength={3}
            maxLength={500}
            autoFocus={action !== "transfer"}
          />
        </label>
        {error === null ? null : (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <div className="dialog-actions">
          <button
            className="button secondary"
            type="button"
            disabled={pending}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className={
              action === "revoke" ? "button ghost-danger" : "button primary"
            }
            type="submit"
            disabled={
              pending ||
              (action === "transfer" &&
                (candidates.isLoading || candidates.isError))
            }
          >
            {pending ? "Saving…" : `Confirm ${action}`}
          </button>
        </div>
      </form>
    </div>
  );
}

function AssignmentWindowDialog({
  assignment,
  pending,
  error,
  onClose,
  onSave,
}: {
  readonly assignment: AssignmentItem;
  readonly pending: boolean;
  readonly error: string | null;
  readonly onClose: () => void;
  readonly onSave: (value: {
    readonly startsAt: string;
    readonly endsAt: string | null;
    readonly reason: string;
  }) => void;
}) {
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const startsAt = data.get("starts_at");
    const endsAt = data.get("ends_at");
    const reason = data.get("reason");
    if (typeof startsAt !== "string" || typeof reason !== "string") return;
    onSave({
      startsAt: new Date(startsAt).toISOString(),
      endsAt:
        typeof endsAt === "string" && endsAt !== ""
          ? new Date(endsAt).toISOString()
          : null,
      reason,
    });
  };
  return (
    <div className="dialog-layer" role="presentation">
      <section
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`window-title-${assignment.id}`}
      >
        <div className="dialog-title">
          <div>
            <p className="eyebrow">{assignment.site_name}</p>
            <h2 id={`window-title-${assignment.id}`}>Reporting window</h2>
          </div>
          <button
            className="icon-button"
            type="button"
            aria-label="Close reporting window form"
            disabled={pending}
            onClick={onClose}
          >
            <X aria-hidden="true" />
          </button>
        </div>
        <form onSubmit={submit}>
          <label>
            Active from
            <input
              name="starts_at"
              type="datetime-local"
              defaultValue={dateTimeLocalValue(assignment.starts_at)}
              required
            />
          </label>
          <label>
            Active until (optional)
            <input
              name="ends_at"
              type="datetime-local"
              defaultValue={dateTimeLocalValue(assignment.ends_at)}
            />
            <small>Clear this field to keep the window open ended.</small>
          </label>
          <label>
            Change reason
            <textarea
              name="reason"
              minLength={3}
              maxLength={500}
              defaultValue="Reporting window updated"
              required
            />
          </label>
          {error === null ? null : (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}
          <div className="dialog-actions">
            <button
              className="button secondary"
              type="button"
              disabled={pending}
              onClick={onClose}
            >
              Cancel
            </button>
            <button className="button primary" type="submit" disabled={pending}>
              {pending ? "Saving…" : "Save window"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

function dateTimeLocalValue(value: string | null): string {
  if (value === null) return "";
  const date = new Date(value);
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

interface AllowanceLedgerResponse {
  readonly items: readonly {
    readonly id: string;
    readonly entry_kind: "grant" | "consume" | "adjustment" | "revoke_unused";
    readonly quantity: number;
    readonly actor_display_name: string;
    readonly reason: string;
    readonly policy_version: number;
    readonly created_at: string;
  }[];
  readonly balance: {
    readonly granted: number;
    readonly consumed: number;
    readonly revoked_unused: number;
    readonly available: number;
  };
  readonly window: {
    readonly starts_at: string;
    readonly ends_at: string | null;
    readonly assignment_state: string;
    readonly expired: boolean;
  };
}

function AllowanceHistory({
  assignment,
}: {
  readonly assignment: AssignmentItem;
}) {
  const [open, setOpen] = useState(false);
  const ledger = useQuery({
    queryKey: ["allowance-ledger", assignment.id],
    queryFn: () =>
      api<AllowanceLedgerResponse>(
        `/assignments/${assignment.id}/allowance-ledger`,
      ),
    enabled: open,
  });
  return (
    <section className="allowance-history">
      <button
        className="allowance-history-toggle"
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <History aria-hidden="true" />
        <span>Allowance history</span>
        <ChevronDown aria-hidden="true" />
      </button>
      {open ? (
        ledger.isLoading ? (
          <StatePanel
            kind="loading"
            title="Loading allowance history"
            detail="Reading the immutable slot ledger."
          />
        ) : ledger.isError || ledger.data === undefined ? (
          <StatePanel
            kind="error"
            title="Allowance history unavailable"
            detail="No balance was changed. Try loading the ledger again."
            action={<RetryButton onClick={() => void ledger.refetch()} />}
          />
        ) : (
          <AllowanceLedger data={ledger.data} />
        )
      ) : null}
    </section>
  );
}

function AllowanceLedger({ data }: { readonly data: AllowanceLedgerResponse }) {
  return (
    <div className="allowance-ledger">
      <div className="allowance-ledger-summary">
        <span>
          <strong>{data.balance.available}</strong> available
        </span>
        <span>{data.balance.granted} granted</span>
        <span>{data.balance.consumed} used</span>
        <span>{data.balance.revoked_unused} revoked unused</span>
      </div>
      {data.balance.available === 0 ? (
        <p className="allowance-ledger-notice warning">
          Allowance exhausted. No post slots remain.
        </p>
      ) : null}
      {data.window.expired ? (
        <p className="allowance-ledger-notice danger">
          Reporting window expired {formatDate(data.window.ends_at)}. New
          evidence is blocked; schedule the next assignment window before more
          posting.
        </p>
      ) : data.window.ends_at === null ? null : (
        <p className="allowance-ledger-notice">
          Reporting window ends {formatDate(data.window.ends_at)}.
        </p>
      )}
      {data.items.length === 0 ? (
        <StatePanel
          kind="empty"
          title="No allowance entries"
          detail="No post allowance has been granted for this assignment."
        />
      ) : (
        <ol className="allowance-ledger-entries">
          {data.items.map((entry) => (
            <li key={entry.id}>
              <span>
                <strong>{allowanceEntryLabel(entry.entry_kind)}</strong>
                <small>
                  {entry.reason} · By {entry.actor_display_name} ·{" "}
                  {formatDate(entry.created_at)} · Policy v
                  {entry.policy_version}
                </small>
              </span>
              <b>
                {entry.quantity > 0 ? `+${entry.quantity}` : entry.quantity}
              </b>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function allowanceEntryLabel(
  kind: AllowanceLedgerResponse["items"][number]["entry_kind"],
): string {
  return {
    grant: "Slots granted",
    consume: "Post slot used",
    adjustment: "Slots added",
    revoke_unused: "Unused slots revoked",
  }[kind];
}
